import React, { useState, useEffect } from 'react';
import { MaintenanceNotification, MaintenanceNotificationSettings } from '../../types/maintenance';
import { api } from '../../utils/api';

interface MaintenanceNotificationsProps {
  onSettingsUpdate?: () => void;
}

export const MaintenanceNotifications: React.FC<MaintenanceNotificationsProps> = ({ onSettingsUpdate }) => {
  const [notifications, setNotifications] = useState<MaintenanceNotification[]>([]);
  const [settings, setSettings] = useState<MaintenanceNotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [newThreshold, setNewThreshold] = useState<number>(60);

  useEffect(() => {
    loadNotifications();
    loadSettings();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      loadNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await api.get<MaintenanceNotification[]>('/maintenance-notifications');
      setNotifications(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get<MaintenanceNotificationSettings>('/maintenance-notifications/settings');
      if (response.data) {
        setSettings(response.data);
        setNewThreshold(response.data.thresholdMinutes);
      } else {
        // Set default settings if none exist
        setSettings({
          id: '',
          thresholdMinutes: 60,
          updatedAt: new Date().toISOString(),
          updatedBy: ''
        });
        setNewThreshold(60);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      // Set default settings on error
      setSettings({
        id: '',
        thresholdMinutes: 60,
        updatedAt: new Date().toISOString(),
        updatedBy: ''
      });
      setNewThreshold(60);
    }
  };

  const updateThreshold = async () => {
    try {
      const response = await api.put<MaintenanceNotificationSettings>('/maintenance-notifications/settings', {
        thresholdMinutes: newThreshold
      });

      setSettings(response.data);
      setEditingThreshold(false);
      setError(null);
      
      if (onSettingsUpdate) {
        onSettingsUpdate();
      }
    } catch (err) {
      console.error('Error updating threshold:', err);
      setError('Failed to update threshold');
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with Settings */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Notificações de Sessões de Longa Duração
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Notificações ativas para sessões de manutenção que excedem o limite
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {editingThreshold ? (
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  value={newThreshold}
                  onChange={(e) => setNewThreshold(parseInt(e.target.value))}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">minutos</span>
                <button
                  onClick={updateThreshold}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Salvar
                </button>
                <button
                  onClick={() => {
                    setEditingThreshold(false);
                    setNewThreshold(settings?.thresholdMinutes || 60);
                  }}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  Limite: <span className="font-semibold">{settings?.thresholdMinutes || 60} minutos</span>
                </span>
                <button
                  onClick={() => setEditingThreshold(true)}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Editar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Notifications List */}
      <div className="p-6">
        {notifications.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma notificação ativa</h3>
            <p className="mt-1 text-sm text-gray-500">
              Todas as sessões de manutenção estão dentro do limite
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="border border-yellow-200 bg-yellow-50 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-yellow-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        Sessão de Manutenção de Longa Duração
                      </h4>
                      <div className="mt-2 text-sm text-gray-700 space-y-1">
                        <p>
                          <span className="font-medium">Funcionário:</span> {notification.employeeCode}
                        </p>
                        <p>
                          <span className="font-medium">Máquina:</span> {notification.machineCode}
                        </p>
                        <p>
                          <span className="font-medium">Duração:</span>{' '}
                          <span className="text-yellow-700 font-semibold">
                            {formatDuration(notification.duration)}
                          </span>
                          {' '}(limite: {formatDuration(notification.threshold)})
                        </p>
                        <p className="text-xs text-gray-500">
                          Detectado em: {formatTimestamp(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
